import axios from 'axios';
import { getUrl } from '../service-utils';
import authHeader from '../auth-header';

const BASE = `${getUrl()}admin/ingestion`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type JobStatus =
  | 'queued'
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'dead_letter'
  | 'cancelled';

export type SourceType =
  | 'tourism_site'
  | 'web_page'
  | 'youtube_video'
  | 'ai_result'
  | 'image'
  | 'destination_images'
  | 'destination_ai_summary'
  | 'destination_seasonality'
  | 'destination_activities'
  | 'destination_attractions'
  | 'destination_dining';

export interface JobEvent {
  at: string;
  level: 'info' | 'warn' | 'error';
  message: string;
}

export interface IngestionJob {
  _id: string;
  jobType: string;
  status: JobStatus;
  priority: string;
  sourceType: SourceType;
  sourceId?: string;
  canonicalSourceKey: string;
  researchId?: string;
  destinationName?: string;
  researchTitle?: string;
  researchLabel?: string;
  primaryDestinationId?: string;
  destinationIds: string[];
  attempts: number;
  maxAttempts: number;
  error?: string;
  events?: JobEvent[];
  startedAt?: string;
  completedAt?: string;
  nextRetryAt?: string;
  createdAt: string;
  updatedAt: string;
  aiSummaryRunMode?: 'full' | 'failed_only' | 'single_section';
  aiSummaryRetrySectionKeys?: string[];
  aiSummaryForceRefreshSectionKeys?: string[];
  aiSummaryRunReason?: string;
}

export interface JobsPage {
  jobs: IngestionJob[];
  pagination: { total: number; page: number; limit: number; pages: number };
}

export interface JobDetailPayload {
  job: IngestionJob;
  source: unknown;
  aiSummarySections?: Array<{
    sectionKey: string;
    status: 'completed' | 'failed' | 'skipped';
    attemptCount: number;
    startedAt?: string;
    completedAt?: string;
    strategy?: 'direct' | 'two_step';
    provenanceLinks?: string[];
    validationErrors?: string[];
    parseError?: string;
  }>;
  aiSummaryUsageBySection?: Record<
    string,
    {
      callCount: number;
      promptTokens: number;
      completionTokens: number;
      totalTokens: number;
      thoughtsTokenCount: number;
      toolUsePromptTokenCount: number;
      toolUseCompletionTokenCount: number;
      finishReasons: string[];
    }
  >;
  runtimeLogs?: Array<{
    _id: string;
    level: 'info' | 'warn' | 'error' | 'debug';
    category: string;
    message: string;
    loggedAt: string;
    context?: Record<string, unknown>;
  }>;
}

export interface SourceTypeMetric {
  sourceType: SourceType;
  counters: Record<string, number>;
  avgLatencyMs: number;
  deadLetterCount: number;
  retryRate: number;
}

export interface QueueStats {
  queueDepth: number;
  totals: Record<string, number>;
  bySourceType: SourceTypeMetric[];
  capturedAt: string;
}

export interface TriggerJobPayload {
  mode: 'free_form' | 'by_ids' | 'force_reingest';
  sourceType?: SourceType;
  url?: string;
  videoId?: string;
  promptText?: string;
  destinationIds?: string[];
  researchId?: string;
  sourceId?: string;
  usagePolicy?: {
    canCrawl?: boolean;
    canStoreEmbeddings?: boolean;
    canUseForRetrieval?: boolean;
  };
  priority?: 'low' | 'normal' | 'high';
}

export interface ListJobsParams {
  status?: JobStatus;
  sourceType?: SourceType;
  destinationId?: string;
  page?: number;
  limit?: number;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const adminIngestionService = {
  async listJobs(params: ListJobsParams = {}): Promise<JobsPage> {
    const res = await axios.get<{ status: string; data: JobsPage }>(
      `${BASE}/jobs`,
      { headers: authHeader(), params }
    );
    return res.data.data;
  },

  async getJobDetail(jobId: string): Promise<JobDetailPayload> {
    const res = await axios.get<{
      status: string;
      data: JobDetailPayload;
    }>(`${BASE}/jobs/${jobId}`, { headers: authHeader() });
    return res.data.data;
  },

  async triggerJob(payload: TriggerJobPayload): Promise<void> {
    await axios.post(`${BASE}/jobs`, payload, { headers: authHeader() });
  },

  async retryJob(jobId: string): Promise<void> {
    await axios.post(
      `${BASE}/jobs/${jobId}/retry`,
      {},
      { headers: authHeader() }
    );
  },

  async retryFailedSections(jobId: string): Promise<void> {
    await axios.post(
      `${BASE}/jobs/${jobId}/retry-failed-sections`,
      {},
      { headers: authHeader() }
    );
  },

  async runSingleSection(
    jobId: string,
    payload: {
      sectionKey: string;
      forceRefresh?: boolean;
      reason?: string;
    }
  ): Promise<void> {
    await axios.post(`${BASE}/jobs/${jobId}/run-section`, payload, {
      headers: authHeader()
    });
  },

  async cancelJob(jobId: string): Promise<void> {
    await axios.post(
      `${BASE}/jobs/${jobId}/cancel`,
      {},
      { headers: authHeader() }
    );
  },

  async getQueueStats(): Promise<QueueStats> {
    const res = await axios.get<{ status: string; data: QueueStats }>(
      `${BASE}/queue-stats`,
      { headers: authHeader() }
    );
    return res.data.data;
  },

  async retryYouTubeTier3(sourceId: string, reason?: string): Promise<void> {
    await axios.post(
      `${BASE}/youtube/${sourceId}/retry-tier3`,
      { reason },
      { headers: authHeader() }
    );
  }
};
